import React from 'react';
import {
  Box, Typography, Stack, Card, Avatar, IconButton, Chip, Divider,
  BottomNavigation, BottomNavigationAction, Button,
  List, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  Timeline as ProgressIcon,
  GroupAdd as JoinIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Notifications as NotifIcon,
  Security as SecurityIcon,
  Help as HelpIcon,
  ExitToApp as LogoutIcon,
  EmojiEvents as TrophyIcon,
  LocalFireDepartment as FireIcon,
  School as SchoolIcon,
  ChevronRight as ChevronIcon,
} from '@mui/icons-material';
import PhoneFrame, { AutomatrixBanner, NavBar } from './PhoneFrame';
import { APP_COLORS, CLASS_MEMBERSHIPS, CLIENT, STUDENT, IMPACT } from '../sharedMockData';

/**
 * Student profile: name, avatar, class memberships, achievements summary,
 * settings list.
 */

const CLASSES = CLASS_MEMBERSHIPS.map(c => ({
  name: c.name,
  teacher: c.teacher,
  app: c.app,
  color: APP_COLORS[c.appKey] || '#546e7a',
}));

const ACHIEVEMENT_STATS = [
  { icon: <FireIcon sx={{ fontSize: 18, color: '#ff6b35' }} />, label: 'Day streak', value: String(IMPACT.streakDays) },
  { icon: <TrophyIcon sx={{ fontSize: 18, color: '#ffb300' }} />, label: 'Badges', value: String(IMPACT.badges) },
  { icon: <SchoolIcon sx={{ fontSize: 18, color: '#1976d2' }} />, label: 'Courses', value: String(IMPACT.courses) },
];

const SETTINGS = [
  { icon: <NotifIcon />, label: 'Notifications', hint: 'Push + email' },
  { icon: <SecurityIcon />, label: 'Privacy & security', hint: 'Enrolled device' },
  { icon: <SettingsIcon />, label: 'App settings', hint: 'Theme · Language · Accessibility' },
  { icon: <HelpIcon />, label: 'Help & support', hint: '' },
];

const AndroidProfileMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 2 }}>
    <PhoneFrame background="#f6f7fb">
      <AutomatrixBanner />

      {/* App bar */}
      <Box sx={{
        px: 2, pt: 1, pb: 1.25, bgcolor: '#fff',
        display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '1px solid #ececf2',
      }}>
        <IconButton size="small"><BackIcon sx={{ fontSize: 20 }} /></IconButton>
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
          Profile
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small"><EditIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {/* Identity header */}
        <Box sx={{
          px: 2, pt: 2.5, pb: 3,
          background: 'linear-gradient(180deg, #1976d2 0%, #1565c0 100%)',
          color: '#fff',
        }}>
          <Stack alignItems="center" spacing={1}>
            <Avatar sx={{
              width: 72, height: 72, fontSize: '1.5rem', fontWeight: 700,
              bgcolor: '#42a5f5', border: '3px solid #fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            }}>
              {STUDENT.initials}
            </Avatar>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
              {STUDENT.full}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" sx={{ color: '#e3f2fd', fontSize: '0.68rem' }}>
                {STUDENT.grade} · {CLIENT.longName}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label="Student"
                sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700, fontSize: '0.65rem' }}
              />
              <Chip
                size="small"
                label="Enrolled device"
                sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700, fontSize: '0.65rem' }}
              />
            </Stack>
          </Stack>
        </Box>

        {/* Floating stats card */}
        <Box sx={{ px: 2, mt: -2 }}>
          <Card elevation={0} sx={{
            borderRadius: 4, bgcolor: '#fff',
            border: '1px solid #e6e8ef',
            boxShadow: '0 6px 14px rgba(16,24,40,0.06)',
          }}>
            <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
              {ACHIEVEMENT_STATS.map((s) => (
                <Box key={s.label} sx={{ flex: 1, textAlign: 'center', py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.25 }}>
                    {s.icon}
                  </Box>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#6b6f76', mt: 0.25 }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Card>
        </Box>

        {/* Classes */}
        <Box sx={{ px: 2, mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.82rem' }}>
            Class memberships
          </Typography>
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e6e8ef', bgcolor: '#fff' }}>
            {CLASSES.map((c, idx) => (
              <React.Fragment key={c.name}>
                <ListItemButton sx={{ py: 1.25 }}>
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: c.color, fontSize: '0.7rem', fontWeight: 700 }}>
                      {c.app.slice(0, 2).toUpperCase()}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 700 }}
                    secondaryTypographyProps={{ fontSize: '0.65rem' }}
                    primary={c.name}
                    secondary={`${c.teacher} · ${c.app}`}
                  />
                  <ChevronIcon sx={{ fontSize: 18, color: '#c3c6cf' }} />
                </ListItemButton>
                {idx < CLASSES.length - 1 && <Divider component="li" sx={{ ml: 6.5 }} />}
              </React.Fragment>
            ))}
          </Card>
        </Box>

        {/* Settings list */}
        <Box sx={{ px: 2, mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.82rem' }}>
            Settings
          </Typography>
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e6e8ef', bgcolor: '#fff' }}>
            <List dense disablePadding>
              {SETTINGS.map((s, idx) => (
                <React.Fragment key={s.label}>
                  <ListItemButton sx={{ py: 1.25 }}>
                    <ListItemIcon sx={{ minWidth: 36, color: '#5f6368' }}>
                      {s.icon}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: '0.64rem' }}
                      primary={s.label}
                      secondary={s.hint || undefined}
                    />
                    <ChevronIcon sx={{ fontSize: 18, color: '#c3c6cf' }} />
                  </ListItemButton>
                  {idx < SETTINGS.length - 1 && <Divider component="li" sx={{ ml: 6 }} />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        </Box>

        {/* Logout */}
        <Box sx={{ px: 2, mt: 2, mb: 2 }}>
          <Button
            fullWidth
            startIcon={<LogoutIcon />}
            sx={{
              borderRadius: 28, textTransform: 'none', fontWeight: 700,
              color: '#c62828', bgcolor: '#fff',
              border: '1px solid #ffcdd2',
              py: 1,
              '&:hover': { bgcolor: '#ffebee' },
            }}
          >
            Sign out
          </Button>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#8a8d93', fontSize: '0.6rem' }}>
            AchieveDXP · v1.0.0 (build 42) · Managed by {CLIENT.name} IT
          </Typography>
        </Box>
      </Box>

      <BottomNavigation
        showLabels
        value={3}
        sx={{
          borderTop: '1px solid #ececf2',
          bgcolor: '#fff',
          height: 56,
          '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
          '& .MuiBottomNavigationAction-label': { fontSize: '0.62rem', fontWeight: 600 },
        }}
      >
        <BottomNavigationAction label="Dashboard" icon={<HomeIcon />} />
        <BottomNavigationAction label="Progress" icon={<ProgressIcon />} />
        <BottomNavigationAction label="Join Class" icon={<JoinIcon />} />
        <BottomNavigationAction label="Profile" icon={<PersonIcon />} />
      </BottomNavigation>

      <NavBar />
    </PhoneFrame>
  </Box>
);

export default AndroidProfileMockup;
